import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { CATEGORIES } from "@/lib/categories";
import { toast } from "sonner";

const FIELDS = [
  { name: "practice_name", label: "Practice / organization name", required: true },
  { name: "specialty", label: "Specialty / focus" },
  { name: "first_name", label: "First name (provider)" },
  { name: "last_name", label: "Last name (provider)" },
  { name: "address", label: "Address" },
  { name: "city", label: "City" },
  { name: "state", label: "State", default: "CO" },
  { name: "zip", label: "ZIP" },
  { name: "phone", label: "Phone" },
  { name: "email", label: "Public email" },
  { name: "website", label: "Website" },
  { name: "hours", label: "Hours of operation" },
  { name: "insurance_accepted", label: "Insurance accepted" },
];

export default function NewListingPage() {
  const [form, setForm] = useState({
    type: "provider",
    category: "Medical Doctors",
    state: "CO",
    accepts_medicaid: false,
    accepts_medicare: false,
    description: "",
    services: [],
  });
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.practice_name) {
      toast.error("Practice name required");
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form };
      // strip empty strings
      Object.keys(payload).forEach((k) => {
        if (payload[k] === "") delete payload[k];
      });
      const { data } = await api.post("/listings/submit", payload);
      toast.success("Submitted! We'll review and email you.");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="container-page py-12" data-testid="new-listing-page">
      <span className="chip">New listing</span>
      <h1 className="mt-3 font-display text-4xl font-bold text-primary lg:text-5xl">Submit a new listing</h1>
      <p className="mt-2 max-w-2xl text-foreground/70">A manager will review your submission and publish it once approved.</p>

      <form onSubmit={submit} className="mt-10 grid max-w-3xl gap-5" data-testid="new-listing-form">
        <div className="grid gap-4 sm:grid-cols-2">
          <label className="block">
            <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-foreground/70">Type</span>
            <select value={form.type} onChange={(e) => set("type", e.target.value)} className="w-full rounded-md border border-border bg-card px-3 py-2.5 outline-none focus:ring-2 focus:ring-accent" data-testid="form-type">
              <option value="provider">Provider</option>
              <option value="organization">Organization</option>
            </select>
          </label>
          <label className="block">
            <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-foreground/70">Category</span>
            <select value={form.category} onChange={(e) => set("category", e.target.value)} className="w-full rounded-md border border-border bg-card px-3 py-2.5 outline-none focus:ring-2 focus:ring-accent" data-testid="form-category">
              {CATEGORIES.map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
            </select>
          </label>
        </div>

        {FIELDS.map((f) => (
          <label key={f.name} className="block">
            <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-foreground/70">
              {f.label}{f.required ? " *" : ""}
            </span>
            <input
              required={f.required}
              value={form[f.name] || ""}
              onChange={(e) => set(f.name, e.target.value)}
              className="w-full rounded-md border border-border bg-card px-3 py-2.5 outline-none focus:ring-2 focus:ring-accent"
              data-testid={`form-${f.name}`}
            />
          </label>
        ))}

        <label className="block">
          <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-foreground/70">Description</span>
          <textarea
            value={form.description}
            onChange={(e) => set("description", e.target.value)}
            rows={5}
            className="w-full rounded-md border border-border bg-card px-3 py-2.5 outline-none focus:ring-2 focus:ring-accent"
            data-testid="form-description"
          />
        </label>

        <div className="flex flex-wrap items-center gap-5">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.accepts_medicaid} onChange={(e) => set("accepts_medicaid", e.target.checked)} className="h-4 w-4 accent-accent" data-testid="form-medicaid" />
            Accepts Medicaid
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.accepts_medicare} onChange={(e) => set("accepts_medicare", e.target.checked)} className="h-4 w-4 accent-accent" data-testid="form-medicare" />
            Accepts Medicare
          </label>
        </div>

        <button type="submit" disabled={submitting} className="btn-primary mt-4 w-fit" data-testid="form-submit">
          {submitting ? "Submitting…" : "Submit for review"}
        </button>
      </form>
    </div>
  );
}
